import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';

// CRITICAL IMPORTS FOR RENDERING
import '@kitware/vtk.js/Rendering/Profiles/Geometry';
import '@kitware/vtk.js/Rendering/OpenGL/RenderWindow';

import vtkGenericRenderWindow from '@kitware/vtk.js/Rendering/Misc/GenericRenderWindow';
import vtkSTLReader from '@kitware/vtk.js/IO/Geometry/STLReader';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';

// Interaction imports
import vtkInteractorStyleManipulator from '@kitware/vtk.js/Interaction/Style/InteractorStyleManipulator';
import vtkMouseCameraTrackballRotateManipulator from '@kitware/vtk.js/Interaction/Manipulators/MouseCameraTrackballRotateManipulator';
import vtkMouseCameraTrackballPanManipulator from '@kitware/vtk.js/Interaction/Manipulators/MouseCameraTrackballPanManipulator';
import vtkMouseCameraTrackballZoomManipulator from '@kitware/vtk.js/Interaction/Manipulators/MouseCameraTrackballZoomManipulator';
import { useGetSTLFilesByStudy } from '../api-client';
import { LoadingIndicatorProgress, Svg } from '@ohif/ui';
import { Icons, Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@ohif/ui-next';

export const STLViewer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const studyInstanceId = searchParams.get('StudyInstanceUIDs');

  const containerRef = useRef(null);
  const vtkContext = useRef(null);
  const [activeTool, setActiveTool] = useState('rotate');
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    data: stlFiles,
    isLoading,
    isError,
  } = useGetSTLFilesByStudy({
    studyInstanceId,
    enabled: Boolean(studyInstanceId),
  });

  const meshes = React.useMemo(() => {
    if (isLoading) return [];

    if (!isError && stlFiles?.data?.results?.length > 0) {
      return stlFiles.data.results.map(result => {
        let color = [1, 1, 1]; // default white
        const name = (result.file_name || '').toLowerCase();
        if (name.includes('kidney'))
          color = [1, 0, 0]; // red
        else if (name.includes('liver'))
          color = [1, 1, 0]; // yellow
        else if (name.includes('tumor')) color = [1, 1, 1]; // white

        return { file: result.file_url, color, opacity: 1.0 };
      });
    }

    // Fallback to local files
    return [
      {
        file: `/${studyInstanceId}/plaque.stl`,
        color: [1, 1, 0],
        opacity: 1.0,
      }, // yellow
      {
        file: `/${studyInstanceId}/artery.stl`,
        color: [1, 0, 0],
        opacity: 1.0,
      }, // red
      {
        file: `/${studyInstanceId}/calcification.stl`,
        color: [1, 1, 1],
        opacity: 1.0,
      }, // White
    ];
  }, [stlFiles, isLoading, isError, studyInstanceId]);

  useEffect(() => {
    if (!containerRef.current || isLoading) return;

    // Initialize the render window
    const genericRenderWindow = vtkGenericRenderWindow.newInstance({
      background: [0, 0, 0],
    });
    genericRenderWindow.setContainer(containerRef.current);

    // Set initial size
    genericRenderWindow.resize();

    const renderer = genericRenderWindow.getRenderer();
    const renderWindow = genericRenderWindow.getRenderWindow();

    // Setup Interactor Style
    const interactor = renderWindow.getInteractor();
    const manipulatorStyle = vtkInteractorStyleManipulator.newInstance();
    interactor.setInteractorStyle(manipulatorStyle);

    const updateManipulators = tool => {
      manipulatorStyle.removeAllManipulators();

      // Always allow zoom with right click or scroll
      const zoomManipulator = vtkMouseCameraTrackballZoomManipulator.newInstance();
      zoomManipulator.setButton(3); // Right button
      zoomManipulator.setScrollEnabled(true); // Enable scroll zoom
      manipulatorStyle.addMouseManipulator(zoomManipulator);

      if (tool === 'rotate') {
        const rotateManipulator = vtkMouseCameraTrackballRotateManipulator.newInstance();
        rotateManipulator.setButton(1); // Left button
        manipulatorStyle.addMouseManipulator(rotateManipulator);

        const panManipulator = vtkMouseCameraTrackballPanManipulator.newInstance();
        panManipulator.setButton(1); // Left button
        panManipulator.setShift(true); // Shift + Left to pan
        manipulatorStyle.addMouseManipulator(panManipulator);
      } else if (tool === 'pan') {
        const panManipulator = vtkMouseCameraTrackballPanManipulator.newInstance();
        panManipulator.setButton(1); // Left button
        manipulatorStyle.addMouseManipulator(panManipulator);

        const rotateManipulator = vtkMouseCameraTrackballRotateManipulator.newInstance();
        rotateManipulator.setButton(1); // Left button
        rotateManipulator.setShift(true); // Shift + Left to rotate
        manipulatorStyle.addMouseManipulator(rotateManipulator);
      }
    };

    // Initial setup
    updateManipulators(activeTool);

    // Store context
    vtkContext.current = {
      genericRenderWindow,
      renderWindow,
      updateManipulators,
    };

    const loadMeshes = async () => {
      setIsDownloading(true);
      const promises = meshes.map(({ file, color, opacity }) => {
        const reader = vtkSTLReader.newInstance();
        return reader.setUrl(file).then(() => {
          const mapper = vtkMapper.newInstance();
          mapper.setInputConnection(reader.getOutputPort());

          mapper.setScalarVisibility(false);

          const actor = vtkActor.newInstance();
          actor.setMapper(mapper);

          const prop = actor.getProperty();
          prop.setColor(...color);
          prop.setOpacity(opacity);

          // Boost lighting
          prop.setAmbient(0.2);
          prop.setDiffuse(0.8);
          prop.setSpecular(0.2);

          renderer.addActor(actor);
        });
      });

      try {
        await Promise.all(promises);

        renderer.resetCamera();
        renderWindow.render();
      } catch (err) {
        console.error('Error loading meshes:', err);
      } finally {
        setIsDownloading(false);
      }
    };

    loadMeshes();

    const handleResize = () => {
      genericRenderWindow.resize();
      renderWindow.render();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      genericRenderWindow.delete();
      vtkContext.current = null;
    };
  }, [meshes, isLoading]); // Re-run when meshes change or loading finishes

  // Effect to update manipulators when activeTool changes
  useEffect(() => {
    vtkContext?.current?.updateManipulators(activeTool);
  }, [activeTool]);

  if (isLoading) {
    return (
      <LoadingIndicatorProgress
        className={'h-full w-full'}
        textBlock={undefined}
        progress={undefined}
      />
    );
  }

  return (
    <div className="relative flex h-screen w-screen flex-col">
      {/* Navbar */}
      <div className="bg-secondary-dark relative z-10 flex h-16 items-center px-5 py-2 text-white shadow-md">
        <TooltipProvider>
          <div className="flex items-center gap-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => navigate(-1)}
                  className="-ml-4 rounded-md p-2 transition-colors hover:bg-white/10"
                >
                  <Icons.ArrowLeft className="h-6 w-6" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Back</TooltipContent>
            </Tooltip>
            <Link to={'/'}>
              <Svg
                name="logo-xylexa"
                className="-ml-4 w-40 sm:w-24 md:w-32 lg:w-36"
              />
            </Link>
          </div>

          {/* Tools centered */}
          <div className="absolute left-1/2 flex -translate-x-1/2 items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setActiveTool('rotate')}
                  className={classNames(
                    'flex items-center gap-2 rounded-md px-4 py-1.5 font-bold transition-colors',
                    activeTool === 'rotate' ? 'bg-highlight text-black' : 'hover:bg-white/20'
                  )}
                >
                  <Icons.Tool3DRotate className="h-6 w-6 flex-shrink-0" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Rotate Tool</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setActiveTool('pan')}
                  className={classNames(
                    'flex items-center gap-2 rounded-md px-4 py-1.5 font-bold transition-colors',
                    activeTool === 'pan' ? 'bg-highlight text-black' : 'hover:bg-white/20'
                  )}
                >
                  <Icons.ToolMove className="h-6 w-6 flex-shrink-0" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Pan Tool</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      {/* Viewer Container */}
      <div
        ref={containerRef}
        className="relative w-full flex-1 overflow-hidden bg-black"
      />

      {/* Downloading Overlay */}
      {isDownloading && (
        <div className="absolute inset-0 z-[5] flex items-center justify-center bg-black/50">
          <LoadingIndicatorProgress
            className={'h-full w-full'}
            textBlock={undefined}
            progress={undefined}
          />
        </div>
      )}
    </div>
  );
};
